import { useState } from "react";

import {
  useE2EAgentAdminArtifactLinks,
  useE2EAgentAdminRun,
  useE2EAgentFailureDiagnostics,
  type E2EAgentArtifact,
  type E2EAgentFailureDiagnostics,
} from "../api/admin";
import { useAuth } from "../auth/AuthContext";
import { startArtifactDownload } from "../lib/artifactDownload";
import { duration, when } from "../lib/time";
import RunTraceSummaryPanel from "./RunTraceSummaryPanel";
import RunCheckSummaryPanel from "./RunCheckSummaryPanel";
import { Button, OpenIcon, Status } from "./ui";

function AdminArtifactButton({
  runId,
  artifact,
}: {
  runId: string;
  artifact: E2EAgentArtifact;
}) {
  const links = useE2EAgentAdminArtifactLinks();
  const [error, setError] = useState<string | null>(null);
  const open = async () => {
    setError(null);
    try {
      const result = await links.mutateAsync({ runId, roles: [artifact.role] });
      const link = result.links[0];
      if (!link) throw new Error("artifact capability was not returned");
      startArtifactDownload(link.url);
    } catch (caught) {
      setError(
        caught instanceof Error ? caught.message : "artifact download failed",
      );
    }
  };
  return (
    <div className="e2e-admin-artifact">
      <div className="e2e-admin-artifact__action">
        <Button
          tone="secondary"
          size="sm"
          iconRight={!links.isPending && <OpenIcon />}
          onClick={() => void open()}
        >
          {links.isPending ? "Opening…" : artifact.role.replaceAll("_", " ")}
        </Button>
        <Status tone={artifact.visibility === "admin" ? "neutralBg" : "tint"}>
          {artifact.visibility === "admin" ? "Admin only" : "Launcher safe"}
        </Status>
      </div>
      <small>
        {artifact.filename} ·{" "}
        {Math.max(1, Math.round(artifact.size_bytes / 1024)).toLocaleString()}{" "}
        KB
      </small>
      {error && <span role="alert">{error}</span>}
    </div>
  );
}

export function FailureDiagnosisDetails({
  diagnostics,
}: {
  diagnostics: E2EAgentFailureDiagnostics;
}) {
  return (
    <>
      <dl className="diagnostic-grid">
        <div>
          <dt>Actual source</dt>
          <dd>{diagnostics.diagnosis.source}</dd>
        </div>
        <div>
          <dt>Diagnostic stage</dt>
          <dd>
            <code>{diagnostics.diagnosis.stage}</code>
          </dd>
        </div>
        <div>
          <dt>Error type</dt>
          <dd>
            <code>{diagnostics.diagnosis.error_type}</code>
          </dd>
        </div>
        <div>
          <dt>Sentry event</dt>
          <dd>
            <code>
              {diagnostics.diagnosis.sentry_event_id ?? "not recorded"}
            </code>
          </dd>
        </div>
      </dl>
      <div className="build-detail-alert">
        <strong>Root cause</strong>
        <span>{diagnostics.diagnosis.message}</span>
      </div>
      {diagnostics.diagnosis.checks.length > 0 && (
        <div className="e2e-admin-artifacts">
          {diagnostics.diagnosis.checks.map((check) => (
            <div
              className="e2e-admin-artifact"
              key={`${check.system}-${check.name}-${check.status}`}
            >
              <Status tone={check.status === "warning" ? "warning" : "danger"}>
                {check.system}
              </Status>
              <small>
                {check.name.replaceAll("_", " ")} · {check.status} ·{" "}
                {check.count.toLocaleString()}
              </small>
            </div>
          ))}
        </div>
      )}
      {diagnostics.diagnosis.collection_errors.length > 0 && (
        <div className="build-detail-alert">
          <strong>Incomplete diagnostic collection</strong>
          <span>
            {diagnostics.diagnosis.collection_errors
              .map((item) => `${item.role}: ${item.error_type}`)
              .join(" · ")}
          </span>
        </div>
      )}
    </>
  );
}

export default function AdminRunEvidence({ runId }: { runId: string }) {
  const auth = useAuth();
  if (!auth.canOperate) return null;
  return <AuthorizedAdminRunEvidence runId={runId} />;
}

function AuthorizedAdminRunEvidence({ runId }: { runId: string }) {
  const query = useE2EAgentAdminRun(runId);
  const failed =
    query.data?.state.kind === "terminal" &&
    query.data.state.outcome === "failed";
  const failureDiagnostics = useE2EAgentFailureDiagnostics(runId, failed);

  if (query.isLoading && !query.data) {
    return <div className="state">Loading administrative run evidence…</div>;
  }
  if (query.error && !query.data) {
    return (
      <div className="state err">
        Administrative evidence unavailable: {query.error.message}
      </div>
    );
  }
  const run = query.data;
  if (!run) return null;

  return (
    <div className="e2e-admin-run-evidence">
      <RunCheckSummaryPanel
        runId={runId}
        terminal={run.state.kind === "terminal"}
      />
      {failed && (
        <section className="e2e-admin-evidence">
          <header>
            <div>
              <span className="section-kicker">
                Scrubbed engineering detail
              </span>
              <h3>Failure diagnosis</h3>
            </div>
            {failureDiagnostics.data?.diagnosis.sentry_event_id && (
              <code>{failureDiagnostics.data.diagnosis.sentry_event_id}</code>
            )}
          </header>
          {failureDiagnostics.isLoading && (
            <div className="state">Loading retained diagnosis…</div>
          )}
          {failureDiagnostics.error && (
            <div className="state err">
              Diagnosis unavailable: {failureDiagnostics.error.message}. Private
              artifacts remain available below.
            </div>
          )}
          {failureDiagnostics.data && (
            <FailureDiagnosisDetails diagnostics={failureDiagnostics.data} />
          )}
        </section>
      )}

      <section className="e2e-admin-evidence">
        <header>
          <div>
            <span className="section-kicker">Durable control-plane state</span>
            <h3>Administrative record</h3>
          </div>
          <span>version {run.state_version}</span>
        </header>
        <dl className="diagnostic-grid">
          <div>
            <dt>Workflow</dt>
            <dd>
              <code>{run.workflow_id}</code>
            </dd>
          </div>
          <div>
            <dt>Company</dt>
            <dd>
              <code>{run.company_id}</code>
            </dd>
          </div>
          <div>
            <dt>Created</dt>
            <dd>{when(run.created_at)}</dd>
          </div>
          <div>
            <dt>Wall time</dt>
            <dd>
              {run.finished_at
                ? duration(run.started_at, run.finished_at)
                : "in progress"}
            </dd>
          </div>
          <div>
            <dt>State version</dt>
            <dd>{run.state_version}</dd>
          </div>
          <div>
            <dt>Model version</dt>
            <dd>{run.model_version_id ?? "not published"}</dd>
          </div>
        </dl>
      </section>

      <RunTraceSummaryPanel
        runId={runId}
        terminal={run.state.kind === "terminal"}
      />

      <section className="e2e-admin-evidence">
        <header>
          <div>
            <span className="section-kicker">
              Private and launcher-safe outputs
            </span>
            <h3>Immutable artifacts</h3>
          </div>
          <span>
            {run.artifacts.length} role{run.artifacts.length === 1 ? "" : "s"}
          </span>
        </header>
        {run.artifacts.length > 0 ? (
          <div className="e2e-admin-artifacts">
            {run.artifacts.map((artifact) => (
              <AdminArtifactButton
                key={artifact.role}
                runId={run.id}
                artifact={artifact}
              />
            ))}
          </div>
        ) : (
          <div className="state state--empty">No artifacts are retained.</div>
        )}
      </section>

      <section className="e2e-admin-evidence">
        <header>
          <div>
            <span className="section-kicker">One paid invocation maximum</span>
            <h3>Worker and sandbox attempts</h3>
          </div>
          <span>
            {run.attempts.length} attempt{run.attempts.length === 1 ? "" : "s"}
          </span>
        </header>
        {run.attempts.length > 0 ? (
          run.attempts.map((attempt) => (
            <dl className="diagnostic-grid" key={attempt.attempt}>
              <div>
                <dt>Attempt</dt>
                <dd>{attempt.attempt}</dd>
              </div>
              <div>
                <dt>Worker release</dt>
                <dd>
                  <code>{attempt.worker_release ?? "—"}</code>
                </dd>
              </div>
              <div>
                <dt>Sandbox pod</dt>
                <dd>
                  <code>{attempt.sandbox_pod ?? "—"}</code>
                </dd>
              </div>
              <div>
                <dt>Sandbox image</dt>
                <dd>
                  <code>{attempt.sandbox_image ?? "—"}</code>
                </dd>
              </div>
              <div>
                <dt>Last heartbeat</dt>
                <dd>{when(attempt.last_heartbeat_at)}</dd>
              </div>
              <div>
                <dt>Outcome</dt>
                <dd>{attempt.outcome ?? "active"}</dd>
              </div>
              <div>
                <dt>Cleanup</dt>
                <dd>{attempt.cleanup_status}</dd>
              </div>
            </dl>
          ))
        ) : (
          <div className="state state--empty">
            No worker or sandbox attempt is recorded.
          </div>
        )}
      </section>
    </div>
  );
}
